<template>
  <BaseModalEditCreate
    v-if="modifiedData"
    :mode="mode"
    title="Select visit plan"
    :is-modified="isModified"
    :hide-save-button="!modifiedData.isFromManager"
    @save="onSave"
    @dismiss-changes="onDismissChanges">
    <template #form>
      <div
        class="space-y-2"
        :class="{
          'pointer-events-none':
            !modifiedData.isFromManager || modifiedData.recordMode == RecordMode.Restored,
        }">
        <span v-if="modifiedData.recordMode == RecordMode.Restored" class="text-xs text-red-500">
          Restored records cannot be modified. Save all changes and try again.
        </span>
        <BaseSearchDropdown
          class="relative z-50 px-0"
          @updateSearch="updateSearch"
          @itemSelected="handleVisitPlanSelection"
          @clear-selection="clearVisitPlanSelection"
          v-model="selectedVisitPlan"
          :listItems="filteredVisitPlans"
          :count="visitPlansCount"
          :noResultsText="$t('messages.noResultsText')" />
        <div v-if="modifiedData.visitPlan">
          <span>Preview visit plan:</span>
          <div class="pt-4 space-y-4">
            <div class="space-y-1 pointer-events-none">
              <span class="text-sm font-normal text-left text-gray-500">
                {{ $t('visitPlans.days') }}
              </span>
              <div class="flex flex-row gap-0.5">
                <div v-for="(item, index) in planDays" :key="index">
                  <BaseButton
                    size="xs"
                    :color="item.isSelected ? 'dark' : 'light'"
                    class="font-medium uppercase text-slate-500">
                    {{ item.day }}
                  </BaseButton>
                </div>
              </div>
            </div>

            <div class="space-y-1 pointer-events-none">
              <span class="text-sm font-normal text-left text-gray-500">{{
                $t('visitPlans.name')
              }}</span>
              <BaseInput
                class="text-sm"
                v-model="modifiedData.visitPlan.name"
                disabled
                color="base" />
            </div>

            <div class="flex flex-row gap-6">
              <div class="space-y-1 pointer-events-none">
                <span class="text-sm font-normal text-left text-gray-500">
                  {{ $t('visitPlans.weeklyStep') }}
                </span>
                <BaseInput
                  class="text-sm"
                  v-model="modifiedData.visitPlan.weekStep"
                  disabled
                  color="base"
                  type="number" />
              </div>
              <div class="space-y-1 pointer-events-none">
                <span class="text-sm font-normal text-left text-gray-500">
                  {{ $t('visitPlans.effectiveWeek') }}
                </span>
                <BaseInput
                  class="text-sm"
                  v-model="modifiedData.visitPlan.effectiveWeek"
                  disabled
                  color="base"
                  type="number"
                  flat />
              </div>
            </div>
            <div class="space-y-1 pointer-events-none">
              <span class="text-sm font-normal text-left text-gray-500">
                {{ $t('visitPlans.remarks') }}
              </span>
              <BaseInput
                class="text-sm text-wrap"
                v-model="modifiedData.visitPlan.remarks"
                disabled
                color="base"
                flat />
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #saveButtonText> Select </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { RouteTraderSite, VisitPlan } from '@/types/Routes';
import { RecordMode, type SelectDropdownItem } from '@/types/Base';
import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';
import BaseSearchDropdown from '../common/BaseSearchDropdown.vue';
import BaseInput from '@/components/common/BaseInput.vue';
import BaseButton from '@/components/common/BaseButton.vue';
import { getVisitPlans } from '@/services/api/routes';
import { deepCopy } from '@/utils/data';

const { t: $t } = useI18n({ useScope: 'global' });
const emit = defineEmits(['closeModal', 'save']);
const props = withDefaults(
  defineProps<{
    mode: 'add' | 'edit';
    routeTraderSite: RouteTraderSite;
  }>(),
  {}
);

const modifiedData = ref<RouteTraderSite>(deepCopy<RouteTraderSite>(props.routeTraderSite));
const isModified = ref<boolean>(false);
const selectedVisitPlan = ref<SelectDropdownItem | null>(null);
const visitPlans = ref<VisitPlan[]>([]);
const filteredVisitPlans = ref<SelectDropdownItem[]>([]);
const visitPlansCount = ref(0);

onMounted(async () => {
  if (
    !modifiedData.value.visitPlanId ||
    modifiedData.value.visitPlanId == '00000000-0000-0000-0000-000000000000'
  ) {
    modifiedData.value.visitPlan = {};
    await updateSearch('');
  }
  deserializeVisitPlanCode();
  initDropdowns();
});

watch(
  () => modifiedData.value,
  (newData) => {
    const valueChanged = newData.visitPlanId !== props.routeTraderSite?.visitPlanId;
    isModified.value = valueChanged;
  },
  { deep: true }
);

async function updateSearch(searchInput: string) {
  if (!visitPlans.value || visitPlans.value.length <= 0) {
    visitPlans.value = await getVisitPlans();
    visitPlansCount.value = visitPlans.value.length;
  }
  filteredVisitPlans.value = visitPlans.value
    .filter(
      (x) =>
        !searchInput ||
        x.name.toLowerCase().includes(searchInput.toLowerCase()) ||
        x.code.toLowerCase().includes(searchInput.toLowerCase()) ||
        x.remarks?.toLowerCase().includes(searchInput.toLowerCase())
    )
    .map((x) => ({ value: x.id, name: x.name }));
}

function handleVisitPlanSelection(item: SelectDropdownItem) {
  selectedVisitPlan.value = item;

  if (modifiedData.value && modifiedData.value.visitPlanId !== item.value) {
    const visitplan = visitPlans.value?.find((x) => x.id == item.value);
    if (visitplan) {
      modifiedData.value.visitPlanId = item.value as string;
      modifiedData.value.visitPlan = visitplan;
    }
  }
  deserializeVisitPlanCode();
}

async function clearVisitPlanSelection() {
  selectedVisitPlan.value = null;
  modifiedData.value.visitPlanId = undefined;
  modifiedData.value.visitPlan = {};
  await updateSearch('');
  deserializeVisitPlanCode();
}

function onDismissChanges() {
  modifiedData.value = deepCopy<RouteTraderSite>(props.routeTraderSite);
  emit('closeModal');
}

async function onSave() {
  if (isModified.value) emit('save', modifiedData.value);
  else emit('closeModal');
}

type Days = { day: string; index: number; isSelected: boolean };
const planDays = ref<Days[]>([
  {
    day: `${$t('date.days.1')}`,
    index: 1,
    isSelected: false,
  },
  {
    day: `${$t('date.days.2')}`,
    index: 2,
    isSelected: false,
  },
  {
    day: `${$t('date.days.3')}`,
    index: 3,
    isSelected: false,
  },
  {
    day: `${$t('date.days.4')}`,
    index: 4,
    isSelected: false,
  },
  {
    day: `${$t('date.days.5')}`,
    index: 5,
    isSelected: false,
  },
  {
    day: `${$t('date.days.6')}`,
    index: 6,
    isSelected: false,
  },
  {
    day: `${$t('date.days.0')}`,
    index: 0,
    isSelected: false,
  },
]);

function deserializeVisitPlanCode() {
  if (!modifiedData.value.visitPlanId) {
    planDays.value = planDays.value.map((day) => {
      return { ...day, isSelected: false };
    });
  } else {
    const codeChars = [...(modifiedData.value.visitPlan?.code || '')];
    codeChars.forEach((char, charIndex) => {
      const index = planDays.value.findIndex((x) => x.index == (charIndex + 1) % 7);
      planDays.value[index] = { ...planDays.value[index], isSelected: char === '1' };
    });
  }
}

function initDropdowns() {
  if (modifiedData.value && modifiedData.value.visitPlan && modifiedData.value.visitPlanId) {
    selectedVisitPlan.value = {
      value: modifiedData.value.visitPlanId,
      name: modifiedData.value.visitPlan.name,
    } as SelectDropdownItem;
    if (!filteredVisitPlans.value.find((t) => t.value == selectedVisitPlan.value?.value))
      filteredVisitPlans.value = [...filteredVisitPlans.value, selectedVisitPlan.value];
  }
}
</script>
